import { alpha } from '@mui/system';
import React from 'react';
import { themeObject } from '../../lib';
import ArrayBasedTableRow from './ArrayBasedTableRow';
import BasicTableRow from './BasicTableRow';
import ObjectBasedTableRow from './ObjectBasedTableRow';

export type TableDetailsData = {
    // in this case ANY is correct, we could pass any nested values (strings, numbers, objects, arrays, blobs, etc).
    [key: string]: any;
};

export const tableStyles = {
    externalTable: {
        border: 'none',
        fontFamily: 'inherit',
        borderCollapse: 'separate',
        borderSpacing: '8px ',
        tableLayout: 'auto',
        'tr td': {
            padding: 0,
        },
    },
    innerTable: {
        border: 'none',
        fontFamily: 'inherit',
        borderSpacing: '8px',
        borderCollapse: 'separate',
        tableLayout: 'auto',
        'tr td': {
            padding: 0,
        },
    },
    innerListTable: {
        border: 'none',
        fontFamily: 'inherit',
        borderSpacing: '10.88px 8px',
        borderCollapse: 'separate',
        tableLayout: 'auto',
        'tr td': {
            padding: 0,
        },
    },
    colorKey: {
        backgroundColor: themeObject.palette.grayScale[100],
        color: themeObject.palette.grayScale[800],
        borderRadius: '4px',
        fontFamily: 'Red Hat Text',
        fontWeight: '600',
        fontSize: '16px',
        lineHeight: '21.17px',
        textAlign: 'center',
        height: '29px',
        width: '140px',
    },
    colorValue: {
        backgroundColor: alpha(themeObject.palette.grayScale[100], 0.6),
        color: themeObject.palette.grayScale[800],
        borderRadius: '4px',
        lineBreak: 'anywhere',
        fontFamily: 'Red Hat Text',
        fontWeight: '600',
        fontSize: '14px',
        lineHeight: '18.52px',
        letter: '0.1px',
        textAlign: 'center',
        height: '29px',
        maxWidth: '570px',
    },
    nestedColorKey: {
        border: `solid 1px ${themeObject.palette.grayScale[300]}`,
        backgroundColor: themeObject.palette.grayScale[200],
        color: themeObject.palette.grayScale[800],
        borderRadius: '4px',
        fontFamily: 'Red Hat Text',
        fontWeight: '600',
        fontSize: '16px',
        lineHeight: '21.17px',
        textAlign: 'center',
        height: '29px',
        width: '140px',
    },
    nestedColorValue: {
        border: `solid 1px ${themeObject.palette.grayScale[300]}`,
        backgroundColor: themeObject.palette.grayScale[50],
        color: themeObject.palette.grayScale[800],
        borderRadius: '4px',
        lineBreak: 'anywhere',
        fontFamily: 'Red Hat Text',
        fontWeight: '600',
        fontSize: '14px',
        lineHeight: '18.52px',
        letter: '0.1px',
        textAlign: 'center',
        height: '29px',
    },
};

export const isNull = (value: string): boolean => {
    return value === null;
};

export const isPrimitive = (value: string): boolean => {
    // null is also a primitive type
    if (isNull(value)) return true;

    // returns true if value is not an object or function.
    return !(typeof value == 'object' || typeof value == 'function');
};

// We want to return a string representation of the current primitive or display NULL is value is missing.
export const getFormattedCellValue = (value: string): string => {
    return isNull(value) ? 'NULL' : String(value);
};

// Returns the type of component that will dynamically render based on data type.
export const renderTableRowsByDataType = (
    dataValue: any,
    label?: string
): React.JSX.Element => {
    if (Array.isArray(dataValue)) {
        return <ArrayBasedTableRow values={dataValue} label={label} />;
    }
    if (isPrimitive(dataValue)) {
        return <BasicTableRow value={dataValue} label={label} />;
    }
    return <ObjectBasedTableRow value={dataValue} label={label} />;
};
